import { z } from "zod";

// ───────────────────────── Cede Arena prize-pool rake ─────────────────────────
//
// Platform fee taken off a competition's prize pool before it's paid to the
// winner (Cede Arena's revenue). Configured per competition in the Solo CMS,
// snapshotted onto the campaign at creation, and never edited once the
// competition is live. Applied at the single rank-1 payout choke point; the net
// math lives in `applyArenaRake` (@cede/utils) so the API and every display
// surface derive gross/rake/net identically.
//
// Kept in its own file (not the cede-arena index) so the blueprint/private DTOs
// can import the schema without a cycle back through the barrel.

/** Basis-point divisor: 10000 bps = 100%. */
export const ARENA_RAKE_BPS_DIVISOR = 10_000;
/** Minimum rake: 0% (no fee). */
export const ARENA_RAKE_BPS_MIN = 0;
/** Maximum rake: 50%. */
export const ARENA_RAKE_BPS_MAX = 5_000;
/** Rake granularity: 0.25% = 25 bps — matches the CMS input's 0.25 step. */
export const ARENA_RAKE_BPS_STEP = 25;

/**
 * A rake in basis points: an integer in [0, 5000] (0%–50%) on a 25-bps grid
 * (0.25% precision). Basis points keep the value all-integer so there's no
 * float drift between the CMS input, the snapshot column, and the payout math.
 */
export const ArenaRakeBpsSchema = z
  .number()
  .int()
  .min(ARENA_RAKE_BPS_MIN, { error: "rake must be at least 0%" })
  .max(ARENA_RAKE_BPS_MAX, { error: "rake must be at most 50%" })
  .refine((bps) => bps % ARENA_RAKE_BPS_STEP === 0, {
    error: "rake must be a multiple of 0.25% (25 bps)",
  });

// ─────────────────── Cede Arena creator rake (private competitions) ───────────
//
// A competition creator's OWN cut, set per private room and stacked on top of
// the platform rake above (DEV-632). Capped lower than the platform rake so the
// combined take stays reasonable. Same 0.25% grid.

/** Maximum creator rake: 20%. */
export const ARENA_CREATOR_RAKE_BPS_MAX = 2_000;

/**
 * A creator rake in basis points: an integer in [0, 2000] (0%–20%) on the same
 * 25-bps (0.25%) grid as the platform rake.
 */
export const ArenaCreatorRakeBpsSchema = z
  .number()
  .int()
  .min(ARENA_RAKE_BPS_MIN, { error: "creator rake must be at least 0%" })
  .max(ARENA_CREATOR_RAKE_BPS_MAX, { error: "creator rake must be at most 20%" })
  .refine((bps) => bps % ARENA_RAKE_BPS_STEP === 0, {
    error: "creator rake must be a multiple of 0.25% (25 bps)",
  });
